import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { utcInputFromNow, utcInputToIso } from '../../utils/utcDateTime';
import AnnouncementForm from './AnnouncementForm';
import 'react-quill/dist/quill.snow.css';
import './announcementModal.css';

const isBodyEmpty = html => {
  const text = (html || '')
    .replace(/<img[^>]*>/gi, 'x')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();
  return !text;
};

const createInitialFormState = () => {
  const now = utcInputFromNow();

  return {
    name: '',
    description: '',
    imageUrl: '',
    startTime: now,
    endTime: now,
    active: false,
  };
};

const CreateAnnouncementModal = ({ show, onHide, onCreate, token }) => {
  const [formData, setFormData] = useState(() => createInitialFormState());
  const [dateError, setDateError] = useState('');
  const [nameError, setNameError] = useState('');
  const [bodyError, setBodyError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (show) {
      setFormData(createInitialFormState());
      setDateError('');
      setNameError('');
      setBodyError('');
      setIsSaving(false);
    }
  }, [show]);

  // Accepts the data to validate so handleChange can pass the NEXT form state —
  // validating this render's formData would lag one edit behind (stale closure).
  const validateDates = (data = formData) => {
    if (data.endTime <= data.startTime) {
      setDateError('End time must be after start time.');
      return false;
    }
    setDateError('');
    return true;
  };

  const validateForm = () => {
    let valid = validateDates();
    if (!formData.name.trim()) {
      setNameError('Name is required.');
      valid = false;
    } else {
      setNameError('');
    }
    if (isBodyEmpty(formData.description)) {
      setBodyError('Announcement content is required.');
      valid = false;
    } else {
      setBodyError('');
    }
    return valid;
  };

  const handleChange = (field, value) => {
    const next = { ...formData, [field]: value };
    setFormData(next);
    if (field === 'startTime' || field === 'endTime') {
      validateDates(next);
    }
    if (field === 'name' && value.trim()) {
      setNameError('');
    }
    if (field === 'description' && !isBodyEmpty(value)) {
      setBodyError('');
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      const startUTC = utcInputToIso(formData.startTime);
      const endUTC = utcInputToIso(formData.endTime);
      if (!startUTC || !endUTC) {
        setDateError('Start and end must be valid UTC times.');
        setIsSaving(false);
        return;
      }

      const announcementData = {
        ...formData,
        startTime: startUTC,
        endTime: endUTC,
        imageUrl: formData.imageUrl || null,
      };
      await onCreate(announcementData);
      onHide();
    } catch (error) {
      console.error('Error creating announcement:', error);
      alert('Failed to create announcement. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="xl" dialogClassName="announcement-modal">
      <Modal.Header closeButton>
        <Modal.Title>Create Announcement</Modal.Title>
      </Modal.Header>
      <Modal.Body className="py-4">
        <AnnouncementForm
          formData={formData}
          onChange={handleChange}
          dateError={dateError}
          nameError={nameError}
          bodyError={bodyError}
          token={token}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={isSaving}>
          {isSaving ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Saving...
            </>
          ) : (
            'Save'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateAnnouncementModal;
